import { useEffect } from "react"
import type { ReactElement } from "react"
import { connect } from "react-redux"
import { usePageTracking } from "../../../../../hooks"
import { AnalyticsPageCategory, AnalyticsPageType } from "../../../../../models"

import {
  createRegion,
  fetchAllLicences,
  fetchChapterListByLicenceIds,
  fetchRegion,
  loadDefaultRegion,
  updateRegion
} from "../../../../../redux"

import type { AppState, AppThunkDispatch, Chapter, Licence, Region, RegionActions } from "../../../../../redux"
import { BackButton, PageContainer } from "../../../../molecules"
import { RegionManagerForm } from "../../../../organisms"

export interface IRegionManagerPageProps {
  region: Region
  chapters: Chapter[]
  licences: Licence[]
  fetchRegion: (id: string) => void
  fetchDefaultRegion: () => void
  createRegion: (region: Region) => void
  updateRegion: (region: Region) => void
  fetchChapters: (licenceIds: string[]) => void
  fetchLicences: () => void
}

export interface IRegionManagerRouteParams {
  regionId?: string
}

function RegionManagerPageView(props: IRegionManagerPageProps & IRegionManagerRouteParams): ReactElement {
  const {
    regionId,
    region,
    chapters,
    licences,
    fetchRegion,
    fetchDefaultRegion,
    createRegion,
    updateRegion,
    fetchChapters,
    fetchLicences
  } = props

  usePageTracking(AnalyticsPageCategory.ContentManager, AnalyticsPageType.Region, regionId)
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchLicences()
    if (regionId && regionId !== region.id) {
      fetchRegion(regionId)
    } else if (!regionId) {
      fetchDefaultRegion()
    }
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (region.id && region.licenceIds && region.licenceIds.length > 0 && chapters && chapters.length === 0) {
      fetchChapters(region.licenceIds)
    }
  }, [region])

  function hasLoaded(): boolean {
    if (regionId === region.id || (!regionId && licences)) {
      return true
    }
    return false
  }

  function handleLicencesChanged(licenceIds: string[]) {
    fetchChapters(licenceIds)
  }

  function handleSubRegionSave(region: Region): void {
    if (region.id) {
      updateRegion(region)
    } else {
      createRegion(region)
    }
  }

  return (
    <PageContainer hasLoaded={hasLoaded()} id="subregion-manager-page" smallPadding>
      <BackButton />
      {licences && (
        <RegionManagerForm
          region={region}
          chapters={chapters}
          licences={licences}
          saveRegion={handleSubRegionSave}
          onLicenceIdsChanged={handleLicencesChanged}
        />
      )}
    </PageContainer>
  )
}

function mapStateToProps(state: AppState, ownProps: IRegionManagerRouteParams) {
  return {
    regionId: ownProps.regionId,
    region: state.region.region,
    chapters: state.chapters.chapters ? state.chapters.chapters : [],
    licences: state.licences.licences
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<RegionActions>) {
  return {
    fetchRegion: (id: string) => dispatch(fetchRegion(id)),
    fetchDefaultRegion: () => dispatch(loadDefaultRegion()),
    createRegion: (region: Region) => dispatch(createRegion(region)),
    updateRegion: (region: Region) => dispatch(updateRegion(region)),
    fetchChapters: (licenceIds: string[]) => dispatch(fetchChapterListByLicenceIds(licenceIds)),
    fetchLicences: () => dispatch(fetchAllLicences())
  }
}

export const RegionManagerPage = connect(mapStateToProps, mapDispatchToProps)(RegionManagerPageView)
